/**
 * "Email me a summary" (SKE-4166, phase 6) — client half.
 *
 * The demo's closing ask, and the only one that leaves something behind after
 * the tab closes. The visitor gets a recap of the board they just worked, a
 * link back into the demo, and the two ways forward.
 *
 * Same transport reasoning as `demoLead.ts`: a plain `fetch` at
 * `VITE_DEMO_LEAD_API_URL` (the schedugram-server origin, which is where
 * HubSpot, the mailer and Turnstile live), NOT the shared axios instance, whose
 * baseURL and interceptors belong to the review API. Anonymous — `credentials`
 * stays at the default `same-origin`, so no cookie is ever sent cross-origin.
 *
 * The counts are the visitor's own client-side tally, derived from the demo
 * store like everything else in the demo. The server clamps and reconciles
 * them; nothing here is authoritative and nothing here touches a real post.
 */

import type { DemoAttribution } from './demoAttribution'
import { getDemoAttribution, getDemoSessionId } from './demoAttribution'

/** Path on schedugram-server. Kept in one place; the server mirrors it. */
export const DEMO_SUMMARY_PATH = '/public/demo-summary'

/**
 * The endpoint base — the same server that hosts the lead endpoint, so it is
 * the same variable. Unset means the demo's server-side half is not configured
 * for this build and the summary CTA is not offered (see
 * `isDemoSummaryConfigured`) rather than shown as something that can only fail.
 */
export const getDemoSummaryEndpoint = (): string | null => {
  const base = import.meta.env.VITE_DEMO_LEAD_API_URL?.trim()

  if (!base) {
    return null
  }

  return `${base.replace(/\/+$/, '')}${DEMO_SUMMARY_PATH}`
}

export const isDemoSummaryConfigured = (): boolean =>
  Boolean(import.meta.env.VITE_DEMO_LEAD_API_URL?.trim())

/**
 * The demo URL the recap links back to. The SPA's own hash route is stripped:
 * it is per-visitor noise, and the server only honours https Sked hosts anyway.
 */
const currentPageUrl = (): string => {
  const location = globalThis.window?.location
  if (!location) {
    return ''
  }
  return `${location.origin}${location.pathname}${location.search}`
}

export interface DemoSummaryPayload {
  email: string
  /** Cloudflare Turnstile token; omitted when no site key is configured. */
  turnstileToken?: string
  sessionId: string
  /** The client the visitor personalized the board to, when they did. */
  brandName?: string
  vertical?: string
  demoUrl: string
  total: number
  approved: number
  requestChange: number
  attribution: DemoAttribution
}

export interface BuildDemoSummaryPayloadInput {
  email: string
  turnstileToken?: string
  brandName?: string
  counts: { total: number; approved: number; requestChange: number }
  /** Overrides for tests; production reads the live browser context. */
  sessionId?: string
  demoUrl?: string
  attribution?: DemoAttribution
}

/**
 * Pure payload builder — the shape the server validates against, assembled in
 * one place so it can be tested without a DOM or a network.
 */
export const buildDemoSummaryPayload = (
  input: BuildDemoSummaryPayloadInput
): DemoSummaryPayload => {
  const attribution = input.attribution ?? getDemoAttribution()

  return {
    email: input.email.trim().toLowerCase(),
    ...(input.turnstileToken ? { turnstileToken: input.turnstileToken } : {}),
    sessionId: input.sessionId ?? getDemoSessionId(),
    ...(input.brandName ? { brandName: input.brandName } : {}),
    ...(attribution.vertical ? { vertical: attribution.vertical } : {}),
    demoUrl: input.demoUrl ?? currentPageUrl(),
    total: input.counts.total,
    approved: input.counts.approved,
    requestChange: input.counts.requestChange,
    attribution,
  }
}

export type DemoSummaryFailureReason =
  | 'not-configured'
  | 'invalid-email'
  | 'captcha'
  | 'rate-limited'
  | 'network'
  | 'server'

export type DemoSummaryResult =
  | { ok: true }
  | { ok: false; reason: DemoSummaryFailureReason; status?: number }

/** Map the server's status code onto something the UI can speak about. */
const failureForStatus = (status: number): DemoSummaryFailureReason => {
  if (status === 429) return 'rate-limited'
  if (status === 400 || status === 422) return 'invalid-email'
  if (status === 403) return 'captcha'
  return 'server'
}

export const submitDemoSummary = async (
  payload: DemoSummaryPayload
): Promise<DemoSummaryResult> => {
  const endpoint = getDemoSummaryEndpoint()

  if (!endpoint) {
    return { ok: false, reason: 'not-configured' }
  }

  let response: Response
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  } catch {
    return { ok: false, reason: 'network' }
  }

  if (!response.ok) {
    let reason = failureForStatus(response.status)
    try {
      const body = (await response.json()) as { code?: string }
      if (body?.code === 'captcha-failed') {
        reason = 'captcha'
      } else if (body?.code === 'invalid-email') {
        reason = 'invalid-email'
      }
    } catch {
      // Non-JSON body — the status-derived reason stands.
    }
    return { ok: false, reason, status: response.status }
  }

  return { ok: true }
}
